import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Search, Tag, X, Bookmark, BookmarkCheck, Eye, EyeOff, Info, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  fetchRssFeeds, 
  fetchRssFeedSources, 
  markRssItemRead, 
  saveRssItem,
  RssFeedItem,
  RssFeedSource
} from '../lib/rss.service';

interface RSSFeedProps {
  onSelectItem?: (item: RssFeedItem) => void;
  showControls?: boolean;
  maxItems?: number;
  defaultCategory?: string;
}

const RSSFeed: React.FC<RSSFeedProps> = ({ 
  onSelectItem, 
  showControls = true,
  maxItems = 10,
  defaultCategory
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory || 'all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());
  const refreshTimeoutRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  // Financial topic categories
  const topicCategories = {
    'Market & Investments': [
      'Market Analysis',
      'Stocks & Bonds',
      'ETFs & Mutual Funds',
      'Commodities & Forex',
      'Cryptocurrency',
      'Real Estate'
    ],
    'Economic Indicators': [
      'GDP Growth',
      'Inflation Rates',
      'Interest Rates',
      'Employment Data',
      'Market Volatility'
    ],
    'Retirement & Planning': [
      'Retirement Strategies',
      '401(k) Plans',
      'IRA Accounts',
      'Social Security Benefits',
      'Pension Planning'
    ],
    'Personal Finance': [
      'Budgeting Tips',
      'Debt Management',
      'Savings Strategies',
      'Credit & Lending',
      'Investment Education'
    ]
  };

  // Trending topics (could be dynamically generated based on frequency)
  const trendingTopics = [
    'Market Analysis',
    'Interest Rates',
    'Retirement Planning',
    'Market Volatility',
    'Cryptocurrency'
  ];

  // Fetch RSS feed sources
  const { data: sources = [] } = useQuery({
    queryKey: ['rssFeedSources'],
    queryFn: fetchRssFeedSources,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Fetch RSS feed items
  const { 
    data: feedItems = [], 
    isLoading, 
    isError, 
    error,
    refetch
  } = useQuery({
    queryKey: ['rssFeeds', selectedCategory, selectedSource],
    queryFn: () => fetchRssFeeds({
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      sourceId: selectedSource !== 'all' ? selectedSource : undefined,
      limit: 50
    }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Mark item as read mutation
  const markReadMutation = useMutation({
    mutationFn: ({ itemId, isRead }: { itemId: string; isRead: boolean }) => 
      markRssItemRead(itemId, isRead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rssFeeds'] });
    },
    onError: (error) => {
      toast.error(`Failed to update read status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Save item mutation
  const saveItemMutation = useMutation({
    mutationFn: ({ itemId, isSaved }: { itemId: string; isSaved: boolean }) => 
      saveRssItem(itemId, isSaved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rssFeeds'] });
      queryClient.invalidateQueries({ queryKey: ['savedRssItems'] });
    },
    onError: (error) => {
      toast.error(`Failed to ${saveItemMutation.variables?.isSaved ? 'save' : 'unsave'} item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Handle manual refresh
  const handleManualRefresh = () => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    refetch();
    setLastRefreshTime(new Date());
    toast.success('RSS feeds refreshed');
  };

  // Extract relevant topics from content
  const extractTopics = (item: RssFeedItem): string[] => {
    const content = `${item.title} ${item.description || ''}`;
    const allTopics = Object.values(topicCategories).flat();
    
    return allTopics.filter(topic => 
      content.toLowerCase().includes(topic.toLowerCase())
    );
  };

  // Filter items based on search and topics
  const filteredItems = feedItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(searchTerm.toLowerCase());

    const itemTopics = extractTopics(item);
    const matchesTopics = selectedTopics.length === 0 ||
      selectedTopics.some(topic => itemTopics.includes(topic));

    return matchesSearch && matchesTopics;
  });

  const displayItems = showAll ? filteredItems : filteredItems.slice(0, maxItems);

  // Handle topic selection
  const toggleTopic = (topic: string) => {
    setSelectedTopics(prev => 
      prev.includes(topic)
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  // Handle item click
  const handleItemClick = (item: RssFeedItem) => {
    if (onSelectItem) {
      onSelectItem(item);
    }
    
    // Mark as read if not already
    if (!item.is_read) {
      markReadMutation.mutate({ itemId: item.id, isRead: true });
    }
  };

  // Handle save/unsave
  const handleSaveToggle = (e: React.MouseEvent, item: RssFeedItem) => {
    e.stopPropagation();
    saveItemMutation.mutate({ itemId: item.id, isSaved: !item.is_saved });
  };

  // Handle read/unread
  const handleReadToggle = (e: React.MouseEvent, item: RssFeedItem) => {
    e.stopPropagation();
    markReadMutation.mutate({ itemId: item.id, isRead: !item.is_read });
  };

  // Get unique categories from sources
  const categories = ['all', ...new Set(sources.map(source => source.category))];

  if (isLoading) {
    return (
      <div className="animate-pulse bg-white rounded-lg shadow-sm p-6 space-y-4">
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center text-gray-500">
          <p>{error instanceof Error ? error.message : 'Failed to load news feed'}</p>
          <button
            onClick={handleManualRefresh}
            className="mt-2 text-primary hover:text-primary-dark flex items-center justify-center mx-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Latest Financial News</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Last updated: {format(lastRefreshTime, 'h:mm a')}
            </span>
            <button
              onClick={handleManualRefresh}
              className="text-primary hover:text-primary-dark"
              title="Refresh feed"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>

        {showControls && (
          <div className="mb-6 space-y-4">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search news and topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border rounded-lg py-2 px-3 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.filter(c => c !== 'all').map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="border rounded-lg py-2 px-3 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Sources</option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Trending Topics */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Trending Topics</h3>
              <div className="flex flex-wrap gap-2">
                {trendingTopics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => toggleTopic(topic)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                      selectedTopics.includes(topic)
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Tag className="h-4 w-4 mr-1" />
                    {topic}
                    {selectedTopics.includes(topic) && (
                      <X 
                        className="h-4 w-4 ml-1" 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTopic(topic);
                        }} 
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Topic Categories */}
            <div className="space-y-6">
              {Object.entries(topicCategories).map(([category, topics]) => (
                <div key={category} className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-900">{category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {topics.map((topic) => (
                      <button
                        key={topic}
                        onClick={() => toggleTopic(topic)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          selectedTopics.includes(topic)
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {topic}
                        {selectedTopics.includes(topic) && (
                          <X 
                            className="h-4 w-4 ml-1" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTopic(topic);
                            }} 
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* News Items */}
        <div className="space-y-6">
          {displayItems.length > 0 ? (
            displayItems.map((item, index) => {
              const itemTopics = extractTopics(item);
              
              return (
                <div
                  key={item.id || index}
                  className={`${
                    index !== displayItems.length - 1 ? 'border-b border-gray-100 pb-6' : ''
                  } ${item.is_read ? 'opacity-75' : ''} cursor-pointer hover:bg-gray-50 -mx-6 px-6`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary mb-2 flex items-start pr-16">
                      {item.title}
                    </h3>
                    <div className="flex space-x-2 shrink-0">
                      <button
                        onClick={(e) => handleReadToggle(e, item)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={item.is_read ? "Mark as unread" : "Mark as read"}
                      >
                        {item.is_read ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleSaveToggle(e, item)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={item.is_saved ? "Unsave" : "Save"}
                      >
                        {item.is_saved ? (
                          <BookmarkCheck className="h-5 w-5 text-primary" />
                        ) : (
                          <Bookmark className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title="Open in new tab"
                      >
                        <ExternalLink className="h-5 w-5 text-gray-500" />
                      </a>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="font-medium text-gray-700">{item.source?.name || item.source}</span>
                    <span className="mx-2">•</span>
                    <span>{format(new Date(item.published_at), 'MMM d, yyyy')}</span>
                    {item.source?.category && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{item.source.category}</span>
                      </>
                    )}
                  </div>
                  {itemTopics.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {itemTopics.map((topic) => (
                        <span
                          key={topic}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <Info className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No news stories found</p>
              <p className="text-sm text-gray-400">
                Try adjusting your filters or search terms
              </p>
            </div>
          )}
        </div>
        
        {filteredItems.length > maxItems && !showAll && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
            >
              Show More
              <ArrowRight className="ml-1 h-4 w-4" />
            </button>
          </div>
        )}
        
        {showAll && filteredItems.length > maxItems && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setShowAll(false)}
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
            >
              Show Less
              <X className="ml-1 h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RSSFeed;